/**
 * PEN-01 (2026-10-10, Doom's UI review + the FSU@LOU live run): which teams Penelope may BUY, switched beside each
 * team's book instead of the old "lean" selector. Side A buys the away team (she rests NO bids on the home team's
 * market); side B buys the home team. A side that is off never adds — she still sells back on it — so switching a
 * side off stops accrual without abandoning inventory (relay `armA` / `armB`, decide.ts sizeFor).
 *
 * One state per game screen: the ticket's switches and Game Detail's book-side switches are the same two switches.
 * Before a game is attached they are a draft the ticket's Run sends; once attached a click PATCHes the relay. Opening a
 * panel never arms anything: the draft starts with both sides off.
 *
 * The screen-level provider polls only while a consumer is mounted (the ticket's Penelope tab, an expanded book), so
 * Game Detail does not gain a background poll.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GameRow } from '@/views/ValueDashboard/types';
import { penelopeEventTicker, usePenelope, type PenelopeControl } from './usePenelope';

export type PenelopeSide = 'A' | 'B';

/** The Kalshi series Penelope runs on (relay PENELOPE_SERIES, relay/src/services/penelope/config.ts). */
export const PENELOPE_EVENT_PREFIXES: readonly string[] = ['KXNFLGAME-', 'KXNCAAFGAME-', 'KXMLBGAME-', 'KXNBAGAME-', 'KXWNBAGAME-', 'KXNHLGAME-', 'KXATPMATCH-', 'KXWTAMATCH-'];
/** The sport a Penelope event is (mirror of the relay's PENELOPE_SERIES), for the per-sport saved default. */
export type PenelopeSport = 'nfl' | 'ncaaf' | 'mlb' | 'nba' | 'wnba' | 'nhl' | 'atp' | 'wta';
const PENELOPE_SPORT_BY_PREFIX: ReadonlyArray<[string, PenelopeSport]> = [
  ['KXNFLGAME-', 'nfl'], ['KXNCAAFGAME-', 'ncaaf'], ['KXMLBGAME-', 'mlb'], ['KXNBAGAME-', 'nba'], ['KXWNBAGAME-', 'wnba'], ['KXNHLGAME-', 'nhl'],
  ['KXATPMATCH-', 'atp'], ['KXWTAMATCH-', 'wta'],
];
export function penelopeSportOfEvent(eventTicker: string | null | undefined): PenelopeSport | null {
  if (!eventTicker) return null;
  return PENELOPE_SPORT_BY_PREFIX.find(([p]) => eventTicker.startsWith(p))?.[1] ?? null;
}
export const PENELOPE_SPORT_LABEL: Record<PenelopeSport, string> = { nfl: 'NFL', ncaaf: 'CFB', mlb: 'MLB', nba: 'NBA', wnba: 'WNBA', nhl: 'NHL', atp: 'ATP', wta: 'WTA' };

export interface PenelopeSides {
  control: PenelopeControl;
  /** What each switch shows: the attached game's armA / armB (absent = on), else the draft. */
  armed: Record<PenelopeSide, boolean>;
  setSide(side: PenelopeSide, on: boolean): void;
}

interface Shared {
  sides: PenelopeSides;
  eventTicker: string | null;
  register(): () => void;
}

const SidesContext = createContext<Shared | null>(null);

function useOwnSides(game: GameRow | null, enabled: boolean): PenelopeSides {
  const control = usePenelope(game, enabled);
  const [draft, setDraft] = useState<Record<PenelopeSide, boolean>>({ A: false, B: false });
  // A draft belongs to one game: when the screen moves to another event in place, it starts over with both sides off,
  // so a tick on one game never arms a side on the next.
  const eventTicker = game ? penelopeEventTicker(game) : null;
  useEffect(() => {
    setDraft({ A: false, B: false });
  }, [eventTicker]);
  const cfg = control.game?.config ?? null;
  const armA = cfg ? cfg.armA !== false : draft.A;
  const armB = cfg ? cfg.armB !== false : draft.B;
  const armed = useMemo(() => ({ A: armA, B: armB }), [armA, armB]);
  const attached = cfg !== null;
  const { patch } = control;
  const setSide = useCallback(
    (side: PenelopeSide, on: boolean) => {
      if (attached) void patch(side === 'A' ? { armA: on } : { armB: on });
      else setDraft((d) => ({ ...d, [side]: on }));
    },
    [attached, patch],
  );
  return { control, armed, setSide };
}

/** Game Detail wraps its moneyline books and its ticket in this so both read one Penelope state. */
export function PenelopeSidesProvider({ game, enabled, children }: { game: GameRow | null; enabled: boolean; children: ReactNode }) {
  const [consumers, setConsumers] = useState(0);
  const register = useCallback(() => {
    setConsumers((c) => c + 1);
    return () => setConsumers((c) => c - 1);
  }, []);
  const sides = useOwnSides(game, enabled && consumers > 0);
  const eventTicker = game ? penelopeEventTicker(game) : null;
  const value = useMemo<Shared>(() => ({ sides, eventTicker, register }), [sides, eventTicker, register]);
  return createElement(SidesContext.Provider, { value }, children);
}

/** The screen's shared state when a provider covers this game's event, else this component's own. */
export function usePenelopeSides(game: GameRow | null, enabled: boolean): PenelopeSides {
  const shared = useContext(SidesContext);
  const eventTicker = game ? penelopeEventTicker(game) : null;
  const useShared = shared !== null && eventTicker !== null && shared.eventTicker === eventTicker;
  const register = useShared ? shared.register : null;
  useEffect(() => (enabled && register ? register() : undefined), [enabled, register]);
  const own = useOwnSides(useShared ? null : game, enabled && !useShared);
  return useShared ? shared.sides : own;
}
